import { GripIcon, MicIcon, MicOffIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { PhoneFilledIcon } from "@/ui/components/voice-icons";
import { CallDrawer } from "./call-drawer";
import { FloatingCard } from "./floating-card";
import { Keypad } from "./keypad";
import { useRemoteDisplay } from "./use-voice-calling";
import { useVoice } from "./voice-provider";

function useCallDuration(startedAt: number | null): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (!startedAt) return null;
  const total = Math.max(0, Math.floor((now - startedAt) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Floating in-call widget. The dark surface is intentional in both themes
 * (standard convention for call UIs); it is not tied to the app theme.
 */
export function CallWidget() {
  const { status, remote, muted, startedAt, endCall, toggleMute, sendDigits } = useVoice();
  const { name, formattedPhoneNumber } = useRemoteDisplay(remote);
  const duration = useCallDuration(startedAt);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const isMobile = useIsMobile();

  const connecting = status === "connecting";

  const statusDot = (
    <span
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        connecting ? "animate-pulse bg-yellow-400" : "bg-green-400",
      )}
    />
  );

  const keypad = keypadOpen ? (
    <div className="mt-3">
      <Keypad onDigit={sendDigits} />
    </div>
  ) : null;

  const controls = (
    <div className="mt-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute microphone" : "Mute microphone"}
          aria-pressed={muted}
          className={cn(
            "flex size-11 items-center justify-center rounded-full transition-colors",
            // Same treatment as the keypad toggle: engaged (muted) is the
            // brighter "pressed" state.
            muted
              ? "bg-white/20 text-zinc-50 hover:bg-white/25"
              : "bg-white/10 text-zinc-300 hover:bg-white/15",
          )}
        >
          {muted ? <MicOffIcon className="size-5" /> : <MicIcon className="size-5" />}
        </button>
        <button
          type="button"
          onClick={() => setKeypadOpen((open) => !open)}
          aria-label={keypadOpen ? "Close keypad" : "Open keypad"}
          aria-pressed={keypadOpen}
          className={cn(
            "flex size-11 items-center justify-center rounded-full transition-colors",
            keypadOpen
              ? "bg-white/20 text-zinc-50 hover:bg-white/25"
              : "bg-white/10 text-zinc-300 hover:bg-white/15",
          )}
        >
          <GripIcon className="size-5" />
        </button>
      </div>
      <button
        type="button"
        onClick={endCall}
        aria-label="End call"
        className="flex h-11 w-14 items-center justify-center rounded-full bg-red-600 text-white transition-colors hover:bg-red-500"
      >
        <PhoneFilledIcon className="size-5 rotate-[135deg]" />
      </button>
    </div>
  );

  // Compact mute + hang up pinned to the docked header bar, so the two
  // essential controls stay reachable while the drawer is collapsed and the
  // full control row is hidden. Only rendered when minimized to avoid
  // duplicating the expanded controls below.
  const dockedControls = (
    <>
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Unmute microphone" : "Mute microphone"}
        aria-pressed={muted}
        className={cn(
          "flex size-8 items-center justify-center rounded-full transition-colors",
          muted
            ? "bg-white/20 text-zinc-50 hover:bg-white/25"
            : "bg-white/10 text-zinc-300 hover:bg-white/15",
        )}
      >
        {muted ? <MicOffIcon className="size-4" /> : <MicIcon className="size-4" />}
      </button>
      <button
        type="button"
        onClick={endCall}
        aria-label="End call"
        className="flex size-8 items-center justify-center rounded-full bg-red-600 text-white transition-colors hover:bg-red-500"
      >
        <PhoneFilledIcon className="size-4 rotate-[135deg]" />
      </button>
    </>
  );

  // Mobile: bottom drawer above all content. No close affordance — the call
  // only ends via End call; sliding down just docks it to the header bar,
  // which doubles as a live status strip (name + timer).
  if (isMobile) {
    return (
      <CallDrawer
        aria-label="Active call"
        minimized={minimized}
        onMinimizedChange={setMinimized}
        headerActions={minimized ? dockedControls : undefined}
        header={
          <div className="flex min-w-0 items-center gap-2.5">
            {statusDot}
            <p className="min-w-0 truncate text-sm font-semibold">
              {name ?? formattedPhoneNumber ?? "Unknown"}
            </p>
            <span className="shrink-0 text-xs tabular-nums text-zinc-400">
              {connecting ? "Calling…" : (duration ?? "0:00")}
            </span>
          </div>
        }
      >
        {name && formattedPhoneNumber ? (
          <p className="truncate text-sm text-zinc-400">{formattedPhoneNumber}</p>
        ) : null}
        {keypad}
        {controls}
      </CallDrawer>
    );
  }

  return (
    <FloatingCard
      aria-label="Active call"
      className="w-72 rounded-2xl border border-white/10 bg-zinc-950/95 p-4 text-zinc-50 shadow-2xl backdrop-blur"
    >
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        {statusDot}
        {connecting ? "Calling…" : <span className="tabular-nums">{duration ?? "0:00"}</span>}
      </div>

      <div className="mt-2 min-w-0">
        <p className="truncate text-base font-semibold">
          {name ?? formattedPhoneNumber ?? "Unknown"}
        </p>
        {name && formattedPhoneNumber ? (
          <p className="truncate text-sm text-zinc-400">{formattedPhoneNumber}</p>
        ) : null}
      </div>

      {keypad}
      {controls}
    </FloatingCard>
  );
}
